/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useSelector } from "react-redux"
import { FoodmaxapipbMartItemMenuDisplayV2Response } from "generated/foodmaxapi.type"
import { useMartItemMenuDisplayV2Query } from "queries"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { isMenuFilterActive } from "utils/menu_helper"
import { AppState } from "store/store.type"

interface UseMartMenuDisplayQueryReturn {
  enablePartialMenu: boolean
  martMenuData: FoodmaxapipbMartItemMenuDisplayV2Response | undefined
  isQueryLoading: boolean
  isQueryFetching: boolean
}

/**
 * Shared mart-menu data source for the department and sub-department
 * overview hooks. Reads the filter state from Redux and fetches the
 * mart-item-menu-display V2 catalogue only when both partial menu is
 * enabled and a filter is active; otherwise the V3 components handle
 * their own data.
 */
export const useMartMenuDisplayQuery = (): UseMartMenuDisplayQueryReturn => {
  const enablePartialMenu = useEnablePartialMenu()

  const {
    merchantID,
    merchantGroupID,
    itemFilter,
    overview: { sellingTime },
  } = useSelector((state: AppState) => state?.menu)

  // Check if filters are active
  const isFilterActive = isMenuFilterActive(itemFilter, sellingTime)

  // Use martItemMenuDisplayV2 query only when:
  // 1. enablePartialMenu is true
  // 2. Filters are active (otherwise V3 components handle their own data)
  const {
    data: martMenuData,
    isLoading: isQueryLoading,
    isFetching: isQueryFetching,
  } = useMartItemMenuDisplayV2Query(
    {
      merchantID,
      merchantGroupID,
    },
    {
      skip: !enablePartialMenu || !merchantID || !isFilterActive,
    },
  )

  return {
    enablePartialMenu,
    martMenuData,
    isQueryLoading,
    isQueryFetching,
  }
}
